import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Container, 
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Euro as EuroIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Storage as StorageIcon,
  Headset as HeadsetIcon
} from '@mui/icons-material';
import landingTheme from '../../theme/landingTheme';
import LandingLayout from '../../components/LandingLayout';

const PricingPage = () => {
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPricing();
  }, []);

  const loadPricing = async () => {
    try {
      const response = await axios.get('/api/pricing');
      // Trasforma i dati dal database nel formato della UI
      const formattedPlans = response.data.map(plan => ({
        name: plan.display_name,
        description: plan.description,
        monthlyPrice: plan.monthly_price,
        yearlyPrice: plan.yearly_price,
        popular: plan.is_popular,
        color: plan.plan_name === 'basic' ? landingTheme.colors.primaryLight :
               plan.plan_name === 'professional' ? landingTheme.colors.primary :
               landingTheme.colors.primaryDark,
        features: plan.features // Array diretto dal database
      }));
      setPlans(formattedPlans);
    } catch (error) {
      console.error('Errore caricamento prezzi:', error);
      // Usa prezzi di fallback
      setPlans(fallbackPlans);
    } finally {
      setLoading(false);
    }
  };

  // Prezzi di fallback in caso di errore
  const fallbackPlans = [
    {
      name: 'Basic',
      monthlyPrice: 49,
      yearlyPrice: 490,
      residents: '20',
      users: '2',
      storage: '10 GB',
      support: 'Email',
      color: landingTheme.colors.primaryLight,
      popular: false,
      features: {
        'Gestione Residenti': true,
        'Anagrafica Completa': true,
        'Calendario Visite': true,
        'Terapie Farmacologiche Base': true,
        'Dashboard Standard': true,
        'Report Base': true,
        'Gestione Staff': false,
        'Turni Automatici': false,
        'App Mobile': false,
        'Integrazione IoT': false,
        'API Access': false,
        'White Label': false,
        'Multi-Struttura': false,
        'Formazione Dedicata': false
      }
    },
    {
      name: 'Premium',
      monthlyPrice: 99,
      yearlyPrice: 990,
      residents: '50',
      users: '5',
      storage: '50 GB',
      support: 'Prioritario',
      color: landingTheme.colors.primary,
      popular: true,
      features: {
        'Gestione Residenti': true,
        'Anagrafica Completa': true,
        'Calendario Visite': true,
        'Terapie Farmacologiche Base': true,
        'Dashboard Standard': true,
        'Report Base': true,
        'Gestione Staff': true,
        'Turni Automatici': true,
        'App Mobile': true,
        'Integrazione IoT': true,
        'API Access': true,
        'White Label': false,
        'Multi-Struttura': false,
        'Formazione Dedicata': false
      }
    },
    {
      name: 'Premium Plus',
      monthlyPrice: 199,
      yearlyPrice: 1990,
      residents: 'Illimitati',
      users: 'Illimitati',
      storage: 'Illimitato',
      support: '24/7 Telefono',
      color: landingTheme.colors.primaryDark,
      popular: false,
      features: {
        'Gestione Residenti': true,
        'Anagrafica Completa': true,
        'Calendario Visite': true,
        'Terapie Farmacologiche Base': true,
        'Dashboard Standard': true,
        'Report Base': true,
        'Gestione Staff': true,
        'Turni Automatici': true,
        'App Mobile': true,
        'Integrazione IoT': true,
        'API Access': true,
        'White Label': true,
        'Multi-Struttura': true,
        'Formazione Dedicata': true
      }
    }
  ];

  const allFeatures = [
    'Gestione Residenti',
    'Anagrafica Completa',
    'Calendario Visite',
    'Terapie Farmacologiche Base',
    'Dashboard Standard',
    'Report Base',
    'Gestione Staff',
    'Turni Automatici',
    'App Mobile',
    'Integrazione IoT',
    'API Access',
    'White Label',
    'Multi-Struttura',
    'Formazione Dedicata'
  ];

  const getPrice = (plan) => {
    return billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
  };

  const getSavings = (plan) => {
    const monthlyTotal = plan.monthlyPrice * 12;
    const yearlySavings = monthlyTotal - plan.yearlyPrice;
    const percentage = Math.round((yearlySavings / monthlyTotal) * 100);
    return { amount: yearlySavings, percentage };
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', background: landingTheme.gradients.primary }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" sx={{ color: 'white', mb: 2 }}>Caricamento prezzi...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <LandingLayout>
      <Box sx={{ minHeight: '100vh', background: landingTheme.gradients.primary }}>
      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ pt: 6, pb: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Chip 
            label="PREZZI TRASPARENTI" 
            sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              color: 'white', 
              fontWeight: 700,
              mb: 3,
              px: 2,
              py: 0.5,
              border: '1px solid rgba(255,255,255,0.3)'
            }} 
          />
          <Typography 
            variant="h2" 
            sx={{ 
              fontWeight: 800,
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              mb: 3
            }}
          >
            Scegli il Piano Perfetto per Te
          </Typography>
          <Typography 
            variant="h5" 
            sx={{ 
              color: 'rgba(255,255,255,0.9)',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              fontWeight: 400,
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6
            }}
          >
            Prezzi chiari, nessun costo nascosto. Inizia con 30 giorni di prova gratuita.
          </Typography>

          {/* Billing Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
            <ToggleButtonGroup
              value={billingCycle}
              exclusive
              onChange={(e, value) => value && setBillingCycle(value)}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                '& .MuiToggleButton-root': {
                  color: 'white',
                  border: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  '&.Mui-selected': {
                    bgcolor: 'white',
                    color: landingTheme.colors.primary,
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.9)'
                    }
                  }
                }
              }}
            >
              <ToggleButton value="monthly">
                Mensile
              </ToggleButton>
              <ToggleButton value="yearly">
                Annuale
              </ToggleButton>
            </ToggleButtonGroup>
            {billingCycle === 'yearly' && (
              <Chip 
                icon={<TrendingDownIcon />}
                label="RISPARMIA FINO AL 17%" 
                sx={{ 
                  bgcolor: '#4caf50',
                  color: 'white',
                  fontWeight: 700,
                  boxShadow: 2
                }}
              />
            )}
          </Box>
        </Box>

        {/* Pricing Cards */}
        <Grid container spacing={4} sx={{ mb: 8 }}>
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card 
                elevation={plan.popular ? 12 : 6}
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  bgcolor: 'rgba(255,255,255,0.95)',
                  backdropFilter: 'blur(10px)',
                  border: plan.popular ? `3px solid ${plan.color}` : '1px solid rgba(0,0,0,0.1)',
                  transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: plan.popular ? 'scale(1.08)' : 'scale(1.03)'
                  }
                }}
              >
                {plan.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -16,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: plan.color,
                      color: 'white',
                      fontWeight: 700,
                      boxShadow: 3,
                      px: 4,
                      py: 1,
                      borderRadius: 2,
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    PIÙ SCELTO
                  </Box>
                )}
                <CardContent sx={{ p: 4, pt: plan.popular ? 6 : 4 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: plan.color, textAlign: 'center', mb: 3 }}>
                    {plan.name}
                  </Typography>
                  
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h2" sx={{ fontWeight: 700, color: '#1a1a1a', display: 'inline' }}>
                      €{getPrice(plan)}
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#666', display: 'inline' }}>
                      /{billingCycle === 'monthly' ? 'mese' : 'anno'}
                    </Typography>
                    {billingCycle === 'yearly' && (
                      <Box sx={{ mt: 1 }}>
                        <Chip 
                          size="small"
                          label={`Risparmi €${getSavings(plan).amount} (${getSavings(plan).percentage}%)`}
                          sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                        />
                      </Box>
                    )}
                  </Box>

                  <Stack spacing={1.5} sx={{ mb: 4 }}>
                    {plan.features && Array.isArray(plan.features) && plan.features.map((feature, idx) => (
                      <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckIcon sx={{ color: plan.color, fontSize: 20, mt: 0.25 }} />
                        <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.6 }}>
                          {feature}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  <Button 
                    variant={plan.popular ? "contained" : "outlined"}
                    fullWidth
                    size="large"
                    onClick={() => navigate('/register')}
                    sx={{ 
                      py: 1.5,
                      fontWeight: 700,
                      bgcolor: plan.popular ? plan.color : 'transparent',
                      borderColor: plan.color,
                      color: plan.popular ? 'white' : plan.color,
                      '&:hover': { 
                        bgcolor: plan.popular ? plan.color : `${plan.color}15`,
                        borderColor: plan.color
                      }
                    }}
                  >
                    Inizia Prova Gratuita
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Comparison Table */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 4, color: 'white', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            Confronto Dettagliato
          </Typography>
          
          <TableContainer component={Paper} elevation={6} sx={{ bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a' }}>Funzionalità</TableCell>
                  {plans.map((plan) => (
                    <TableCell key={plan.name} align="center" sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a1a' }}>
                      {plan.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {allFeatures.map((feature) => (
                  <TableRow key={feature} sx={{ '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.05)' } }}>
                    <TableCell sx={{ fontWeight: 600, color: '#333' }}>{feature}</TableCell>
                    {plans.map((plan) => (
                      <TableCell key={plan.name} align="center">
                        {plan.features[feature] ? (
                          <CheckIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                        ) : (
                          <CloseIcon sx={{ color: '#f44336', fontSize: 28 }} />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* FAQ Pricing */}
        <Card sx={{ p: 6, bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center', mb: 4, color: '#1a1a1a' }}>
            Domande Frequenti sui Prezzi
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: landingTheme.colors.primary }}>
                  ❓ Posso cambiare piano in qualsiasi momento?
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.7 }}>
                  Sì, puoi fare upgrade o downgrade del tuo piano in qualsiasi momento. 
                  Le modifiche sono immediate e il costo viene ricalcolato proporzionalmente.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: landingTheme.colors.primary }}>
                  💳 Quali metodi di pagamento accettate?
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.7 }}>
                  Accettiamo carte di credito (Visa, Mastercard, American Express), 
                  bonifico bancario per piani annuali e PayPal.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: landingTheme.colors.primary }}>
                  🔒 I miei dati sono al sicuro?
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.7 }}>
                  Assolutamente. Utilizziamo crittografia AES-256, backup giornalieri 
                  e siamo conformi GDPR e normative sanitarie italiane.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: landingTheme.colors.primary }}>
                  📞 Il supporto è incluso?
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.7 }}>
                  Sì, tutti i piani includono supporto. Il livello varia: email per Basic, 
                  prioritario per Premium e 24/7 telefonico per Premium Plus.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: landingTheme.colors.primary }}>
                  🎓 È inclusa la formazione?
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.7 }}>
                  I piani Basic e Premium includono video tutorial. 
                  Il piano Premium Plus include formazione personalizzata on-site.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: landingTheme.colors.primary }}>
                  💼 Avete sconti per organizzazioni?
                </Typography>
                <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.7 }}>
                  Sì, offriamo sconti per organizzazioni con più strutture. 
                  Contatta il nostro team vendite per un preventivo personalizzato.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Card>

        {/* Enterprise CTA */}
        <Card sx={{ 
          p: 6, 
          bgcolor: 'rgba(255,255,255,0.15)', 
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.3)',
          textAlign: 'center'
        }}>
          <EuroIcon sx={{ fontSize: 80, color: 'white', mb: 2, filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.3))' }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 2, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
            Hai Bisogno di un Piano Enterprise?
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.9)', mb: 4, textShadow: '1px 1px 2px rgba(0,0,0,0.3)' }}>
            Soluzioni personalizzate per grandi organizzazioni con esigenze specifiche
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ justifyContent: 'center' }}>
            <Button 
              variant="contained" 
              size="large"
              onClick={() => navigate('/contact')}
              sx={{ 
                bgcolor: 'white',
                color: landingTheme.colors.primary,
                fontWeight: 700,
                px: 4,
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)'
                }
              }}
            >
              Contatta Vendite
            </Button>
            <Button 
              variant="outlined"
              size="large"
              onClick={() => navigate('/demo')}
              sx={{ 
                borderColor: 'white',
                borderWidth: 2,
                color: 'white',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                '&:hover': {
                  borderWidth: 2,
                  bgcolor: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              Richiedi Demo
            </Button>
          </Stack>
        </Card>
      </Container>
      </Box>
    </LandingLayout>
  );
};

export default PricingPage;
